import React, { useState } from 'react';
import { useRoleStore } from '@/lib/store';
import { useBranches, useCreateStaff, useStaff, useUpdateStaff } from '@/hooks/use-api';
import type { Role, User } from '@shared/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Pencil, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { getInitials } from './utils';

type StaffFormRole = 'Officer' | 'Manager' | 'Accountant';
const STAFF_ROLES: StaffFormRole[] = ['Manager', 'Officer', 'Accountant'];

export function PersonnelTab() {
  const role = useRoleStore((s) => s.role);
  const isAdmin = role === 'Admin';

  const { data: staffData, isLoading: staffLoading } = useStaff(true);
  const { data: branches } = useBranches(true);
  const createStaff = useCreateStaff();
  const updateStaff = useUpdateStaff();

  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<User | null>(null);
  const [newStaff, setNewStaff] = useState({ name: '', email: '', password: '', role: 'Officer' as StaffFormRole, branchId: '', territory: '' });
  const [editStaffForm, setEditStaffForm] = useState({ name: '', email: '', branchId: '', territory: '', isDeactivated: false });

  const handleCreateStaff = async () => {
    if (!newStaff.name || !newStaff.email || !newStaff.password || !newStaff.branchId) {
      toast.error('Name, email, temporary password, and branch are required'); return;
    }
    try {
      await createStaff.mutateAsync(newStaff);
      toast.success('Personnel record registered successfully');
      setIsStaffModalOpen(false);
      setNewStaff({ name: '', email: '', password: '', role: 'Officer', branchId: '', territory: '' });
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Registration failed.'); }
  };

  const handleEditStaff = async () => {
    if (!editingStaff) return;
    try {
      await updateStaff.mutateAsync({ id: editingStaff.id, data: editStaffForm });
      toast.success('Staff record updated successfully');
      setEditingStaff(null);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed to update staff record'); }
  };

  const handleDeactivate = async (staffId: string, isDeactivated: boolean) => {
    try {
      await updateStaff.mutateAsync({ id: staffId, data: { isDeactivated: !isDeactivated } });
      toast.success(isDeactivated ? 'Personnel account reactivated' : 'Personnel account deactivated');
    } catch { toast.error('Staff status update was rejected.'); }
  };

  const openStaffEditor = (user: User) => {
    setEditingStaff(user);
    setEditStaffForm({ name: user.name || '', email: user.email || '', branchId: user.branchId || '', territory: user.territory || '', isDeactivated: !!user.isDeactivated });
  };

  return (
    <>
      <Card className="overflow-hidden rounded-3xl border border-border bg-card shadow-glass">
        <CardHeader className="flex flex-col items-start justify-between gap-4 border-b bg-muted/30 p-8 lg:flex-row lg:items-center">
          <div>
            <CardTitle className="text-foreground font-black uppercase tracking-tight">Staff Records</CardTitle>
            <CardDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Create, edit, deactivate, and review branch assignments.</CardDescription>
          </div>
          {isAdmin && (
            <Button onClick={() => setIsStaffModalOpen(true)} className="h-11 rounded-xl bg-primary px-6 font-black uppercase tracking-tight shadow-lg shadow-primary/20 hover:bg-primary/90">
              <UserPlus className="mr-2 h-4 w-4" />New Staff
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {staffLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-10 w-10 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-muted/50">
                  <TableRow className="border-border">
                    <TableHead className="pl-8 text-[10px] font-black uppercase tracking-widest text-muted-foreground">Staff</TableHead>
                    <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Role</TableHead>
                    <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Branch</TableHead>
                    <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Territory</TableHead>
                    <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Status</TableHead>
                    <TableHead className="pr-8 text-right text-[10px] font-black uppercase tracking-widest text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffData?.items.map((user) => (
                    <TableRow key={user.id} className="border-border hover:bg-muted/20">
                      <TableCell className="pl-8">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12 border-border">
                            <AvatarImage src={user.avatar || user.image || undefined} />
                            <AvatarFallback className="bg-primary text-primary-foreground font-black">{getInitials(user.name)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-black tracking-tight text-foreground">{user.name}</p>
                            <p className="text-xs font-bold text-muted-foreground">{user.email || 'No email'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Badge className="border-none bg-secondary text-[10px] font-black uppercase tracking-widest">{user.role}</Badge></TableCell>
                      <TableCell className="font-bold text-foreground">{branches?.find((b) => b.id === user.branchId)?.name || user.branchId}</TableCell>
                      <TableCell className="font-bold text-muted-foreground">{user.territory || 'Not Assigned'}</TableCell>
                      <TableCell>
                        <Badge className={user.isDeactivated ? 'border-none bg-destructive/10 text-destructive' : 'border-none bg-emerald-500/10 text-emerald-600'}>
                          {user.isDeactivated ? 'Inactive' : 'Active'}
                        </Badge>
                      </TableCell>
                      <TableCell className="pr-8">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" className="rounded-xl font-black uppercase text-muted-foreground" onClick={() => openStaffEditor(user)}>
                            <Pencil className="mr-2 h-4 w-4" />Edit
                          </Button>
                          <Button variant="ghost" size="sm" className="rounded-xl font-black uppercase text-destructive" onClick={() => handleDeactivate(user.id, !!user.isDeactivated)}>
                            <Trash2 className="mr-2 h-4 w-4" />{user.isDeactivated ? 'Restore' : 'Deactivate'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Staff Modal */}
      <Dialog open={isStaffModalOpen} onOpenChange={setIsStaffModalOpen}>
        <DialogContent className="rounded-[2rem] border-border p-8 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase tracking-tight text-foreground">Register Personnel</DialogTitle>
            <DialogDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Create a staff login with email, temporary password, role, and branch.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-5 py-6 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Full Name</Label>
              <Input placeholder="Employee Name" value={newStaff.name} onChange={(e) => setNewStaff((c) => ({ ...c, name: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
            </div>
            <div className="space-y-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Email</Label>
              <Input type="email" placeholder="employee@perfectmatch.com" value={newStaff.email} onChange={(e) => setNewStaff((c) => ({ ...c, email: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
            </div>
            <div className="space-y-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Temporary Password</Label>
              <Input type="password" placeholder="At least 8 characters" value={newStaff.password} onChange={(e) => setNewStaff((c) => ({ ...c, password: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
            </div>
            <div className="space-y-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Role</Label>
              <Select value={newStaff.role} onValueChange={(v) => setNewStaff((c) => ({ ...c, role: v as StaffFormRole }))}>
                <SelectTrigger className="h-12 rounded-xl border-border bg-muted/30"><SelectValue placeholder="Select Role" /></SelectTrigger>
                <SelectContent className="rounded-xl">
                  {STAFF_ROLES.map((r) => <SelectItem key={r} value={r} className="font-bold">{r === 'Officer' ? 'Field Officer' : r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Branch</Label>
              <Select value={newStaff.branchId} onValueChange={(v) => setNewStaff((c) => ({ ...c, branchId: v }))}>
                <SelectTrigger className="h-12 rounded-xl border-border bg-muted/30"><SelectValue placeholder="Choose Branch" /></SelectTrigger>
                <SelectContent className="rounded-xl">
                  {branches?.map((b) => <SelectItem key={b.id} value={b.id} className="font-bold">{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Territory</Label>
              <Input placeholder="Assigned territory" value={newStaff.territory} onChange={(e) => setNewStaff((c) => ({ ...c, territory: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setIsStaffModalOpen(false)} className="h-12 rounded-xl px-6 text-xs font-black uppercase text-muted-foreground">Cancel</Button>
            <Button onClick={handleCreateStaff} className="h-12 rounded-xl bg-primary px-8 font-black uppercase tracking-tight shadow-lg shadow-primary/20 hover:bg-primary/90" disabled={createStaff.isPending}>
              {createStaff.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Register Staff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Modal */}
      <Dialog open={!!editingStaff} onOpenChange={(open) => !open && setEditingStaff(null)}>
        <DialogContent className="rounded-[2rem] border-border p-8 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase tracking-tight text-foreground">Edit Staff Record</DialogTitle>
            <DialogDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Update identity, assignment, and status.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-5 py-6 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Full Name</Label>
              <Input value={editStaffForm.name} onChange={(e) => setEditStaffForm((c) => ({ ...c, name: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
            </div>
            <div className="space-y-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Email</Label>
              <Input type="email" value={editStaffForm.email} onChange={(e) => setEditStaffForm((c) => ({ ...c, email: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
            </div>
            <div className="space-y-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Branch</Label>
              <Select value={editStaffForm.branchId} onValueChange={(v) => setEditStaffForm((c) => ({ ...c, branchId: v }))}>
                <SelectTrigger className="h-12 rounded-xl border-border bg-muted/30"><SelectValue placeholder="Choose Branch" /></SelectTrigger>
                <SelectContent className="rounded-xl">
                  {branches?.map((b) => <SelectItem key={b.id} value={b.id} className="font-bold">{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="px-1 text-xs font-black uppercase tracking-tight text-foreground">Territory</Label>
              <Input value={editStaffForm.territory} onChange={(e) => setEditStaffForm((c) => ({ ...c, territory: e.target.value }))} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setEditingStaff(null)} className="h-12 rounded-xl px-6 text-xs font-black uppercase text-muted-foreground">Cancel</Button>
            <Button onClick={handleEditStaff} className="h-12 rounded-xl bg-primary px-8 font-black uppercase tracking-tight shadow-lg shadow-primary/20 hover:bg-primary/90" disabled={updateStaff.isPending}>
              {updateStaff.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Staff Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
